import commonUtils from '@kathondvla/sri-client/common-utils';
import { generalSaveError } from '@store/alerts/alertConstants';
import { setAlert } from '@store/alerts/alertsState';
import { fillExpandedClasses, getActivitiesForPlan } from '@store/calendar/calendarHelper';
import { postActivityBatchSaga, postAttachmentsSaga } from '@store/calendar/calendarSagaHelpers';
import { selectActivitiesForPlan } from '@store/calendar/calendarSelectors';
import { selectUngroupedHomePageListIncludingPreviousYearList } from '@store/leerplanList/leerplanListSelectors';
import { getCreatePlanBatch } from '@store/llinkidApis/llinkidApiHelper';
import {
  selectActivitiesData,
  selectHaveActivitiesBeenLoadedRecently,
} from '@store/llinkidApis/llinkidApiSelectors';
import { initActivities, saveLlinkidApiBatch } from '@store/llinkidApis/llinkidApiState';
import {
  selectAllClassesInSchoolyear,
  selectAllCreatorsActiveInSchoolyear,
  selectCurrentSchool,
  selectCurrentSchoolHref,
  selectLoggedInUser,
  selectOrgsTeacherMapForSchoolyear,
} from '@store/userAndSchool/userAndSchoolSelectors';
import { typedSelect, waitForSaga } from '@utils/sagaUtils';
import { getCurriculaForPlan } from '@utils/utils';

import { setCurrentSchoolyear } from '@store/userAndSchool/usersAndSchoolState';
import { logAndCaptureException } from '@utils/logAndCaptureException';
import { isEqual } from 'lodash-es';
import type { PayloadAction } from '@reduxjs/toolkit';
import { call, put, takeEvery } from 'redux-saga/effects';
import { getCopyCalendarBatch, getGoalsToCopy } from './calendarListHelper';
import {
  selectArchiveCalendarBatch,
  selectRecoverCalendarBatch,
  selectRemoveCalendarBatch,
} from './calendarListSelectors';
import {
  copyCalendar,
  copyCalendarClose,
  copyCalendarOpen,
  copyCalendarSetOriginalCalendar,
  createCalendar,
  archiveCalendar,
  deleteCalendar,
  editCalendar,
  undeleteCalendar,
} from './calendarListState';
import type {
  EditCalendarPayload,
  CreateCalendarPayload,
  CalendarKeysPayload,
  CopyCalendarPayload,
  CopyCalendarOpenPayload,
} from './calendarListTypes';
import type { RootState } from '@store/storeSetup';
import type { BatchRequest } from '../../types/sriTypes';

function* editCalendarSaga({ payload }: PayloadAction<EditCalendarPayload>) {
  try {
    const { plans, formValues } = payload;

    const batch: BatchRequest = [];
    const model = { ...formValues };
    const me = yield* typedSelect(selectLoggedInUser);
    const orgs = yield* typedSelect(selectOrgsTeacherMapForSchoolyear);
    const alreadyCreator = model.teachers.some(
      (e) => e.href === me.$$meta.permalink || orgs[e.href]?.includes(me.$$meta.permalink),
    );

    if (me && !alreadyCreator) {
      // only add me if i'm not already a creator (person or in a group)
      model.teachers = [{ href: me.$$meta.permalink }, ...model.teachers];
    }

    const activityPlans = yield* typedSelect(
      (state: RootState) => state.customCurriculaData.activityPlans,
    );

    for (const cl of model.classes) {
      const workingcopy = plans.find((p) => p.class.href === cl.$$meta.permalink);
      if (!workingcopy) {
        throw new Error('Er is iets misgegaan bij het opslaan van het vorderingsplan.');
      }

      const body = {
        ...activityPlans[workingcopy.key],
        ...{
          creators: model.teachers,
          observers: model.shared ?? null,
          title: model.title,
          curricula: model.curriculum,
        },
      };

      batch.push({
        verb: 'PUT' as const,
        href: `/llinkid/activityplanning/activityplans/${workingcopy.key}`,
        body,
      });

      if (Object.keys(formValues.goalTranslationsMap).length) {
        const activities = yield* typedSelect((state: RootState) =>
          selectActivitiesForPlan(state, activityPlans[workingcopy.key]),
        );

        activities.forEach((activity) => {
          if (activity.goals?.length) {
            const goals = getGoalsToCopy(activity.goals, formValues.goalTranslationsMap);
            if (!isEqual(activity.goals, goals)) {
              batch.push({
                verb: 'PUT' as const,
                href: activity.$$meta.permalink,
                body: { ...activity, goals },
              });
            }
          }
        });
      }
    }

    yield postActivityBatchSaga({ payload: { batch, asyncSaving: false } });
  } catch (e) {
    logAndCaptureException(e);
    yield put(setAlert(generalSaveError));
  }
}

function* createCalendarSaga({ payload }: PayloadAction<CreateCalendarPayload>) {
  try {
    const batch = getCreatePlanBatch(payload) as BatchRequest;
    const batchKey = commonUtils.generateUUID();
    yield put(saveLlinkidApiBatch({ batch, batchKey, applyInstantly: false }));
    yield call(
      waitForSaga,
      (state: RootState) => state.customCurriculaData.batches[batchKey].isCompleted === true,
    );
    const plans: string[] = [];
    // get the plan keys for the state go.
    batch.forEach((b) => {
      if (b.href.startsWith('/llinkid/activityplanning/activityplans/')) plans.push(b.body.key);
    });
    globalThis.router.navigate({
      pathname: '/vorderingsplannen',
      search: `ids=${plans.join()}&merge=true`,
    });
  } catch (e) {
    logAndCaptureException(e);
    yield put(setAlert(generalSaveError));
  }
}

function* archiveCalendarSaga({ payload }: PayloadAction<CalendarKeysPayload>) {
  try {
    const { keys } = payload;
    const batch = yield* typedSelect((state: RootState) =>
      selectArchiveCalendarBatch(state, { keys }),
    );
    yield postActivityBatchSaga({ payload: { batch, asyncSaving: false } });
  } catch (e) {
    logAndCaptureException(e);
    yield put(setAlert(generalSaveError));
  }
}

function* deleteCalendarSaga({ payload }: PayloadAction<CalendarKeysPayload>) {
  try {
    const { keys } = payload;
    const batch = yield* typedSelect((state: RootState) =>
      selectRemoveCalendarBatch(state, { keys }),
    );
    yield postActivityBatchSaga({ payload: { batch, asyncSaving: false } });
  } catch (e) {
    logAndCaptureException(e);
    yield put(setAlert(generalSaveError));
  }
}

function* undeleteCalendarSaga({ payload }: PayloadAction<CalendarKeysPayload>) {
  try {
    const { keys } = payload;
    const batch = yield* typedSelect((state: RootState) =>
      selectRecoverCalendarBatch(state, { keys }),
    );
    yield postActivityBatchSaga({ payload: { batch, asyncSaving: false } });
  } catch (e) {
    logAndCaptureException(e);
    yield put(setAlert(generalSaveError));
  }
}

function* copyCalendarSaga({ payload }: PayloadAction<CopyCalendarPayload>) {
  try {
    const { originalCalendar, goalTranslationsMap, classes, cloneCalendar, schoolyear } = payload;

    const me = yield* typedSelect(selectLoggedInUser);
    const schoolHref = yield* typedSelect(selectCurrentSchoolHref);
    const orgs = yield* typedSelect((state: RootState) =>
      selectOrgsTeacherMapForSchoolyear(state, { schoolyear }),
    );
    const alreadyCreator = cloneCalendar.teachers.some(
      (e) => e.href === me.$$meta.permalink || orgs[e.href]?.includes(me.$$meta.permalink),
    );

    if (me && !alreadyCreator) {
      // only add me if i'm not already a creator (person or in a group)
      cloneCalendar.teachers = [{ href: me.$$meta.permalink }, ...cloneCalendar.teachers];
    }
    const [batch, attachments] = getCopyCalendarBatch({
      original: originalCalendar,
      copy: cloneCalendar,
      classesMap: classes,
      goalTranslationsMap,
    }) as [BatchRequest, { items: any[]; files: any[] }];

    yield postActivityBatchSaga({ payload: { batch, asyncSaving: false } });
    yield postAttachmentsSaga({ payload: { attachments, asyncSaving: false } });
    yield put(copyCalendarClose());
    yield put(setCurrentSchoolyear(schoolyear));
    yield put(initActivities({ schoolHref, schoolyear })); // refresh activities to contain the correct attachments.
    yield put(
      setAlert({
        key: 'copy-calendar-success',
        msg: 'Vorderingsplan gekopiëerd.',
        type: 'success',
        showClose: true,
        delay: 5000,
      }),
    );
  } catch (e) {
    logAndCaptureException(e);
    yield put(setAlert(generalSaveError));
  }
}

function* setPlanDataForCopy({ payload }: PayloadAction<CopyCalendarOpenPayload>) {
  const { item } = payload;
  const { plans } = item;
  const school = yield* typedSelect(selectCurrentSchool);
  const allCreators = yield* typedSelect(selectAllCreatorsActiveInSchoolyear);
  const currentSchoolyear: RootState['userAndSchools']['currentSchoolyear'] = yield* typedSelect(
    (state: RootState) => state.userAndSchools.currentSchoolyear,
  );
  const leerplanListWithUngrouped: ReturnType<
    typeof selectUngroupedHomePageListIncludingPreviousYearList
  > = yield* typedSelect((state: RootState) =>
    selectUngroupedHomePageListIncludingPreviousYearList(state, currentSchoolyear),
  );
  const curricula = getCurriculaForPlan(leerplanListWithUngrouped, plans[0]);

  const allClasses = yield* typedSelect(selectAllClassesInSchoolyear);
  const classes = plans
    ?.map((p) => {
      const expandedPlan = fillExpandedClasses(p, allClasses);
      return expandedPlan.class;
    })
    .sort((a, b) => (a.$$displayName > b.$$displayName ? 1 : -1));

  // wait for all the activities to be loaded
  yield call(waitForSaga, (state: RootState) => selectHaveActivitiesBeenLoadedRecently(state));

  const allActivities = yield* typedSelect(selectActivitiesData);
  const plansWithActivities = plans.map((p) => {
    const planActivities = getActivitiesForPlan(p, allActivities);
    return { ...p, activities: planActivities };
  });

  yield put(
    copyCalendarSetOriginalCalendar({
      originalCalendar: {
        ...item,
        plans: plansWithActivities,
        classes,
        curricula,
        school,
        allCreators,
      },
    }),
  );
}

export function* watcherSaga() {
  yield takeEvery(editCalendar, editCalendarSaga);
  yield takeEvery(createCalendar, createCalendarSaga);
  yield takeEvery(archiveCalendar, archiveCalendarSaga);
  yield takeEvery(deleteCalendar, deleteCalendarSaga);
  yield takeEvery(undeleteCalendar, undeleteCalendarSaga);
  yield takeEvery(copyCalendar, copyCalendarSaga);
  yield takeEvery(copyCalendarOpen, setPlanDataForCopy);
}
